import React, { useState, useEffect } from 'react';

interface HoursByCategory {
  category: string;
  totalHours: number;
  percentOfWeek: number;
}

interface HoursByPerson {
  UserId: string;
  Name: string;
  Email: string;
  totalHours: number;
  avgHoursPerDay: number;
}

interface HoursByProject {
  ProjectId: string;
  ProjectTitle: string;
  totalHours: number;
  teamCount: number;
}

interface TeamHoursReportData {
  week: string;
  totalTeamHours: number;
  byCategory: HoursByCategory[];
  byPerson: HoursByPerson[];
  byProject: HoursByProject[];
}

interface TeamHoursReportProps {
  teamId: string;
  teamName: string;
}

export default function TeamHoursReport({ teamId, teamName }: TeamHoursReportProps) {
  const [data, setData] = useState<TeamHoursReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeekStartDate());

  useEffect(() => {
    fetchReport();
  }, [teamId, selectedWeek]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/hours-reporting/team/${teamId}?weekStartDate=${selectedWeek}`);
      const result = await res.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to load report');
      }

      setData(result.data);
      setError('');
    } catch (err: any) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const getPriorWeek = () => {
    const date = new Date(selectedWeek);
    date.setDate(date.getDate() - 7);
    setSelectedWeek(date.toISOString().slice(0, 10));
  };

  const getNextWeek = () => {
    const date = new Date(selectedWeek);
    date.setDate(date.getDate() + 7);
    setSelectedWeek(date.toISOString().slice(0, 10));
  };

  if (loading) {
    return <div style={{ padding: '1rem' }}>Loading report...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '1rem', color: '#d32f2f' }}>
        Error: {error}
      </div>
    );
  }

  if (!data) {
    return <div style={{ padding: '1rem' }}>No data available</div>;
  }

  const weekEnd = new Date(data.week);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const weekRange = `${new Date(data.week).toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>{teamName} - Hours Report</h2>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>
            Week of {weekRange}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={getPriorWeek}
            style={{
              padding: '0.5rem 1rem',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            ← Previous Week
          </button>
          <div style={{ fontSize: '1.2rem', fontWeight: 600, textAlign: 'center' }}>
            {data.totalTeamHours.toFixed(1)} hours logged
          </div>
          <button
            onClick={getNextWeek}
            style={{
              padding: '0.5rem 1rem',
              background: '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Next Week →
          </button>
        </div>
      </div>

      {/* Hours by Category */}
      <div style={{ marginBottom: '2rem' }}>
        <h3>Hours by Category</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          {data.byCategory.map(cat => (
            <div
              key={cat.category}
              style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                {cat.category}
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 600, color: '#333', marginBottom: '0.5rem' }}>
                {cat.totalHours.toFixed(1)}h
              </div>
              <div style={{ fontSize: '0.85rem', color: '#999' }}>
                {cat.percentOfWeek}% of team hours
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hours by Person */}
      <div style={{ marginBottom: '2rem' }}>
        <h3>Hours by Team Member</h3>
        {data.byPerson.length === 0 ? (
          <div style={{ padding: '1rem', color: '#666', background: '#f9f9f9', borderRadius: '4px' }}>
            No hours logged this week
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right' }}>Total Hours</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right' }}>Avg/Day</th>
                </tr>
              </thead>
              <tbody>
                {data.byPerson.map(person => (
                  <tr key={person.UserId} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.75rem' }}>{person.Name}</td>
                    <td style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#666' }}>
                      {person.Email}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>
                      {person.totalHours.toFixed(1)}h
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.9rem' }}>
                      {person.avgHoursPerDay.toFixed(1)}h
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Hours by Project */}
      <div>
        <h3>Hours by Project</h3>
        {data.byProject.length === 0 ? (
          <div style={{ padding: '1rem', color: '#666', background: '#f9f9f9', borderRadius: '4px' }}>
            No project hours logged this week
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Project</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right' }}>Hours</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Contributors</th>
                </tr>
              </thead>
              <tbody>
                {data.byProject.map(project => (
                  <tr key={project.ProjectId} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.75rem' }}>{project.ProjectTitle}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>
                      {project.totalHours.toFixed(1)}h
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      {project.teamCount} {project.teamCount === 1 ? 'person' : 'people'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function getCurrentWeekStartDate(): string {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  const weekStart = new Date(now.setDate(diff));
  return weekStart.toISOString().slice(0, 10);
}
